// Ekipman Zekâsı rapor PDF'leri — jsPDF + autoTable, RobotoMedium (Türkçe), çok sayfalı, sayfa altlığı
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import initRobotoMedium from '../fonts/Roboto-Medium';

initRobotoMedium();

const FONT = 'RobotoMedium';
const PAGE = { a4: { landscape: [297, 210], portrait: [210, 297] }, a3: { landscape: [420, 297], portrait: [297, 420] } };
export const ORANGE = [230, 126, 34];
const GRAY = [107, 114, 128];
const DARK = [31, 41, 55];
// RobotoMedium'da bulunmayan glifler (₺ → ✓ ✗ ⚠ emoji) yerine güvenli karşılıklar
export const glyph = (v) => String(v ?? '').replace(/₺/g, 'TL').replace(/→/g, '»').replace(/←/g, '«').replace(/[✓✔☑]/g, '+').replace(/[✗✘✕]/g, 'x').replace(/[⚠]/g, '!').replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '');

/** Rapor başlat: başlık bandı + alt başlık. Döner ctx {pdf, y, w, h, m}. */
export function startReport({ title, subtitle = '', orientation = 'landscape', pageSize = 'a4', meta = '' }) {
  const [w, h] = PAGE[pageSize]?.[orientation] || PAGE.a4.landscape;
  const pdf = new jsPDF({ orientation, unit: 'mm', format: pageSize });
  pdf.setFont(FONT, 'normal');
  const ctx = { pdf, y: 0, w, h, m: 12, title, meta };
  pdf.setFillColor(...DARK);
  pdf.rect(0, 0, w, 16, 'F');
  pdf.setTextColor(255, 255, 255); pdf.setFontSize(13);
  pdf.text(glyph(title), ctx.m, 10.5);
  pdf.setFontSize(8); pdf.setTextColor(200, 200, 200);
  pdf.text(new Date().toLocaleString('tr-TR'), w - ctx.m, 10.5, { align: 'right' });
  ctx.y = 22;
  if (subtitle) { pdf.setFontSize(9); pdf.setTextColor(...GRAY); ctx.y = writeWrapped(ctx, subtitle, 9) + 2; }
  return ctx;
}

function ensureSpace(ctx, need) {
  if (ctx.y + need > ctx.h - 14) { ctx.pdf.addPage(); ctx.y = 14; }
}

function writeWrapped(ctx, text, size, color = GRAY, indent = 0) {
  const { pdf } = ctx;
  pdf.setFontSize(size); pdf.setTextColor(...color);
  const lines = pdf.splitTextToSize(glyph(text), ctx.w - 2 * ctx.m - indent);
  const lh = size * 0.42;
  for (const ln of lines) { ensureSpace(ctx, lh); pdf.text(ln, ctx.m + indent, ctx.y); ctx.y += lh; }
  return ctx.y;
}

export function addHeading(ctx, text, level = 1) {
  const size = level === 1 ? 12 : level === 2 ? 10.5 : 9.5;
  ensureSpace(ctx, level === 1 ? 32 : 22);
  ctx.y += 3;
  const { pdf } = ctx;
  pdf.setFontSize(size); pdf.setTextColor(...(level === 1 ? ORANGE : DARK));
  pdf.text(glyph(text), ctx.m, ctx.y);
  if (level === 1) { pdf.setDrawColor(...ORANGE); pdf.setLineWidth(0.4); pdf.line(ctx.m, ctx.y + 1.5, ctx.w - ctx.m, ctx.y + 1.5); }
  ctx.y += level === 1 ? 6 : 5;
}

export function addParagraph(ctx, text, opts = {}) {
  writeWrapped(ctx, text, opts.size || 8.5, opts.color || DARK, opts.indent || 0);
  ctx.y += 1.5;
}

export function addBullets(ctx, lines, opts = {}) {
  const size = opts.size || 8.5;
  for (const ln of lines) {
    ensureSpace(ctx, size * 0.42);
    ctx.pdf.setFontSize(size); ctx.pdf.setTextColor(...DARK);
    ctx.pdf.text('•', ctx.m + (opts.indent || 0), ctx.y);
    writeWrapped(ctx, ln, size, DARK, (opts.indent || 0) + 4);
  }
  ctx.y += 1;
}

/** KPI kutuları tek satırda: [{label, value, sub, color:[r,g,b]}] */
export function addKpis(ctx, kpis) {
  const n = kpis.length; if (!n) return;
  const gap = 3; const bw = (ctx.w - 2 * ctx.m - gap * (n - 1)) / n; const bh = 15;
  ensureSpace(ctx, bh + 4);
  kpis.forEach((k, i) => {
    const x = ctx.m + i * (bw + gap);
    ctx.pdf.setFillColor(243, 244, 246); ctx.pdf.setDrawColor(229, 231, 235);
    ctx.pdf.roundedRect(x, ctx.y, bw, bh, 1.5, 1.5, 'FD');
    ctx.pdf.setFontSize(7); ctx.pdf.setTextColor(...GRAY); ctx.pdf.text(k.label, x + 2.5, ctx.y + 4.2);
    const val = glyph(k.value ?? '—');
    let fs = 12;
    ctx.pdf.setFontSize(fs);
    while (fs > 7 && ctx.pdf.getTextWidth(val) > bw - 5) { fs -= 0.5; ctx.pdf.setFontSize(fs); }
    ctx.pdf.setTextColor(...(k.color || DARK)); ctx.pdf.text(val, x + 2.5, ctx.y + 10.2);
    if (k.sub) { ctx.pdf.setFontSize(6.5); ctx.pdf.setTextColor(...GRAY); ctx.pdf.text(glyph(k.sub), x + 2.5, ctx.y + 13.6); }
  });
  ctx.y += bh + 4;
}

/** Tablo: cellStyler(rowIndex, colIndex, raw) → {fillColor, textColor, halign} */
export function addTable(ctx, headers, rows, opts = {}) {
  ensureSpace(ctx, 20);
  autoTable(ctx.pdf, {
    head: [headers.map(glyph)], body: rows.map(r => r.map(glyph)), startY: ctx.y, margin: { left: ctx.m, right: ctx.m, bottom: 14 },
    styles: { font: FONT, fontStyle: 'normal', fontSize: opts.fontSize || 7.5, cellPadding: 1.6, overflow: 'linebreak', lineColor: [220, 220, 220], lineWidth: 0.1, textColor: DARK },
    headStyles: { fillColor: opts.headerColor || DARK, textColor: [255, 255, 255], fontSize: (opts.fontSize || 7.5) + 0.5, halign: 'left' },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    columnStyles: opts.columnStyles || {},
    didParseCell: (d) => {
      if (opts.cellStyler && d.section === 'body') { const st = opts.cellStyler(d.row.index, d.column.index, d.cell.raw); if (st) Object.assign(d.cell.styles, st); }
    },
  });
  ctx.y = ctx.pdf.lastAutoTable.finalY + 5;
}

/** Basit markdown → PDF (başlık, madde, numaralı, tablo, paragraf). Kalın/italik işaretleri temizlenir. */
export function addMarkdown(ctx, md, opts = {}) {
  const clean = (s) => String(s).replace(/\*\*(.*?)\*\*/g, '$1').replace(/__(.*?)__/g, '$1').replace(/`([^`]*)`/g, '$1').replace(/\*(?!\s)(.*?)\*/g, '$1').trim();
  const lines = String(md || '').split('\n');
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const t = raw.trim();
    if (!t) { ctx.y += 1.2; i++; continue; }
    if (/^\|.*\|\s*$/.test(t)) {
      const block = [];
      while (i < lines.length && /^\|.*\|\s*$/.test(lines[i].trim())) { block.push(lines[i].trim()); i++; }
      const cells = (ln) => ln.slice(1, -1).split('|').map(c => clean(c));
      const body = block.filter(ln => !/^\|\s*:?-{2,}/.test(ln));
      if (body.length) addTable(ctx, cells(body[0]), body.slice(1).map(cells), { fontSize: opts.fontSize || 7.5, headerColor: opts.headerColor });
      continue;
    }
    const h = t.match(/^(#{1,4})\s+(.*)$/);
    if (h) { addHeading(ctx, clean(h[2]), Math.min(h[1].length, 3)); i++; continue; }
    const b = t.match(/^[-*•]\s+(.*)$/);
    if (b) { addBullets(ctx, [clean(b[1])], { size: opts.fontSize || 8.5, indent: (raw.match(/^\s*/)[0].length >= 2 ? 4 : 0) }); i++; continue; }
    const num = t.match(/^(\d+)[.)]\s+(.*)$/);
    if (num) {
      ensureSpace(ctx, 4); ctx.pdf.setFontSize(opts.fontSize || 8.5); ctx.pdf.setTextColor(...DARK); ctx.pdf.text(`${num[1]}.`, ctx.m, ctx.y);
      writeWrapped(ctx, clean(num[2]), opts.fontSize || 8.5, DARK, 5); i++; continue;
    }
    if (/^---+$/.test(t)) { ctx.pdf.setDrawColor(220, 220, 220); ensureSpace(ctx, 3); ctx.pdf.line(ctx.m, ctx.y, ctx.w - ctx.m, ctx.y); ctx.y += 3; i++; continue; }
    addParagraph(ctx, clean(t), { size: opts.fontSize || 8.5 });
    i++;
  }
}

/** Görsel (dataURL) tam genişlikte; caption varsa altına küçük yazı (link opsiyonel) */
export function addImage(ctx, dataUrl, { height, caption, link, format = 'JPEG' } = {}) {
  const w = ctx.w - 2 * ctx.m; const h = height || w * 0.46;
  ensureSpace(ctx, h + (caption ? 8 : 3));
  ctx.pdf.addImage(dataUrl, format, ctx.m, ctx.y, w, h);
  ctx.pdf.setDrawColor(220, 220, 220); ctx.pdf.setLineWidth(0.2); ctx.pdf.rect(ctx.m, ctx.y, w, h);
  ctx.y += h + 3.5;
  if (caption) {
    ctx.pdf.setFontSize(7.5); ctx.pdf.setTextColor(...(link ? [37, 99, 235] : GRAY));
    if (link) ctx.pdf.textWithLink(glyph(caption), ctx.m, ctx.y, { url: link }); else ctx.pdf.text(glyph(caption), ctx.m, ctx.y);
    ctx.y += 4;
  }
  ctx.y += 1.5;
}

/** Sayfa altlığı + kaydet */
export function finishReport(ctx, filename) {
  const { pdf } = ctx;
  const n = pdf.internal.getNumberOfPages();
  for (let p = 1; p <= n; p++) {
    pdf.setPage(p);
    pdf.setFontSize(7); pdf.setTextColor(...GRAY);
    pdf.text(`${ctx.title}${ctx.meta ? ' · ' + ctx.meta : ''}`, ctx.m, ctx.h - 6);
    pdf.text(`Sayfa ${p} / ${n}`, ctx.w - ctx.m, ctx.h - 6, { align: 'right' });
  }
  pdf.save(filename);
  return true;
}

// ---- Ekipman Zekâsı'na özel renk haritaları ----
export const RISK_PDF = {
  'İyi': { fillColor: [5, 150, 105], textColor: [255, 255, 255], halign: 'center' },
  'İzle': { fillColor: [234, 179, 8], textColor: [0, 0, 0], halign: 'center' },
  'Riskli': { fillColor: [249, 115, 22], textColor: [255, 255, 255], halign: 'center' },
  'Kritik': { fillColor: [220, 38, 38], textColor: [255, 255, 255], halign: 'center' },
};
export const skorStyle = (raw) => {
  const n = Number(raw);
  if (Number.isNaN(n) || raw === '' || raw === '—') return { textColor: GRAY, halign: 'center' };
  return { fillColor: n >= 75 ? [209, 250, 229] : n >= 50 ? [254, 249, 195] : n >= 25 ? [255, 237, 213] : [254, 226, 226], textColor: DARK, halign: 'center' };
};
export const stamp = () => new Date().toISOString().slice(0, 10);
